import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Clock, Briefcase, RefreshCw, CheckCircle2, XCircle, IndianRupee, Radio, Loader2, Car, PlayCircle, Flag } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const POLL_MS = 4000;

const STATUS_LABEL = { Pending: 'Pending', Accepted: 'Job Accepted', EnRoute: 'En Route', Started: 'Job Started', Finished: 'Job Finished' };
const STATUS_COLOR = {
  Pending:  { bg: 'bg-[#E8F5E9]', text: 'text-[#2E7D32]' },
  Accepted: { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]' },
  EnRoute:  { bg: 'bg-[#DBEAFE]', text: 'text-[#1D4ED8]' },
  Started:  { bg: 'bg-[#EDE9FE]', text: 'text-[#6D28D9]' },
  Finished: { bg: 'bg-neutral-100', text: 'text-neutral-500' },
};
const NEXT_STEP = {
  Accepted: { next: 'EnRoute',  label: 'Start trip',       icon: Car },
  EnRoute:  { next: 'Started',  label: 'Mark started',     icon: PlayCircle },
  Started:  { next: 'Finished', label: 'Finish job',       icon: Flag },
};

const CleanerJobs = () => {
  const nav = useNavigate();
  const { user } = useApp();
  const [tab, setTab] = useState('live'); // live | active | finished
  const [pending, setPending] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const [lastPoll, setLastPoll] = useState(null);
  const [online, setOnline] = useState(true);
  const pollRef = useRef(null);

  const loadAll = async () => {
    try {
      const [p, m] = await Promise.all([
        axios.get(`${API}/cleaner/pending-jobs`, authCfg()),
        axios.get(`${API}/cleaner/my-jobs`, authCfg()),
      ]);
      setPending(p.data);
      setMine(m.data);
      setLastPoll(new Date());
    } catch (e) {
      if (e?.response?.status === 401) nav('/cleaner/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    if (online) pollRef.current = setInterval(loadAll, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [online]);

  const accept = async (id) => {
    if (actioningId) return;
    setActioningId(id);
    setPending((prev) => prev.filter((b) => b.id !== id));
    try {
      const { data } = await axios.post(`${API}/cleaner/accept-job/${id}`, {}, authCfg());
      setMine((prev) => [data, ...prev.filter((b) => b.id !== data.id)]);
      setTab('active');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to accept');
      loadAll();
    } finally {
      setActioningId(null);
    }
  };

  const reject = async (id) => {
    if (actioningId) return;
    setActioningId(id);
    setPending((prev) => prev.filter((b) => b.id !== id));
    try {
      await axios.post(`${API}/cleaner/reject-job/${id}`, {}, authCfg());
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to reject');
      loadAll();
    } finally {
      setActioningId(null);
    }
  };

  const advance = async (id, nextStatus) => {
    if (actioningId) return;
    setActioningId(id);
    try {
      const { data } = await axios.post(`${API}/cleaner/update-status/${id}`, { status: nextStatus }, authCfg());
      setMine((prev) => prev.map((b) => (b.id === id ? data : b)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setActioningId(null);
    }
  };

  const active = mine.filter((b) => ['Accepted', 'EnRoute', 'Started'].includes(b.status));
  const finished = mine.filter((b) => b.status === 'Finished');
  const visible = tab === 'live' ? pending : tab === 'active' ? active : finished;

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-extrabold">Worker Dashboard</h1>
              {online && (
                <span data-testid="cleaner-live-indicator" className="inline-flex items-center gap-1 bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32] animate-pulse" /> Live
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-500 mt-0.5">
              {lastPoll ? `Last update ${lastPoll.toLocaleTimeString()}` : 'Fetching…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-neutral-200 rounded-full px-3 py-1.5">
              <span className={`w-2 h-2 rounded-full ${online ? 'bg-[#2E7D32]' : 'bg-neutral-300'}`} />
              <span className="text-xs font-semibold">{online ? 'Online' : 'Offline'}</span>
              <input type="checkbox" data-testid="cleaner-online-toggle" checked={online} onChange={(e) => setOnline(e.target.checked)} className="w-3 h-3 accent-[#2E7D32]" />
            </label>
            <button data-testid="cleaner-refresh-btn" onClick={loadAll} className="text-xs font-semibold text-[#2E7D32] border border-[#2E7D32] px-3 py-1.5 rounded-full hover:bg-[#E8F5E9] transition-colors inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button onClick={() => nav('/cleaner/earnings')} className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 border border-neutral-200 px-3 py-1.5 rounded-full transition-colors">Earnings</button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <Tab id="live"     active={tab === 'live'}     onClick={setTab} label={`Live (${pending.length})`}  testid="cleaner-tab-live" />
          <Tab id="active"   active={tab === 'active'}   onClick={setTab} label={`My Jobs (${active.length})`} testid="cleaner-tab-active" />
          <Tab id="finished" active={tab === 'finished'} onClick={setTab} label={`Finished (${finished.length})`} testid="cleaner-tab-finished" />
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : visible.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-200 rounded-xl p-10 text-center">
            <Radio className="w-10 h-10 text-neutral-300 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-neutral-700">
              {tab === 'live' ? 'No live jobs right now' : tab === 'active' ? 'No active jobs' : 'No finished jobs yet'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">
              {tab === 'live' ? 'Stay online — new bookings appear here automatically.' : 'Accept a live job to see it here.'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {visible.map((j) => {
              const svc = j.items?.[0]?.name || 'Cleaning service';
              const claiming = actioningId === j.id;
              const badge = STATUS_COLOR[j.status] || STATUS_COLOR.Pending;
              const step = NEXT_STEP[j.status];
              return (
                <div key={j.id} data-testid={`cleaner-job-${j.id}`} className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full ${badge.bg} flex items-center justify-center flex-shrink-0`}>
                      <Briefcase className={`w-4 h-4 ${badge.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{svc}</p>
                      <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{j.address || 'Address unavailable'}</p>
                      <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{j.date} · {j.slot}</p>
                      <p className="text-[11px] text-neutral-400 mt-0.5">Booking · {j.id} · {j.user_name || 'Customer'}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-extrabold text-[#EA580C] flex items-center justify-end"><IndianRupee className="w-3.5 h-3.5" />{j.total}</p>
                      <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${badge.bg} ${badge.text}`}>{STATUS_LABEL[j.status] || j.status}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tab === 'live' && (
                      <>
                        <button
                          data-testid={`cleaner-accept-${j.id}`}
                          onClick={() => accept(j.id)}
                          disabled={claiming}
                          className="flex-1 min-w-[120px] py-2 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1">
                          {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                          {claiming ? 'Processing…' : 'Accept'}
                        </button>
                        <button
                          data-testid={`cleaner-reject-${j.id}`}
                          onClick={() => reject(j.id)}
                          disabled={claiming}
                          className="flex-1 min-w-[120px] py-2 rounded-lg bg-white border border-neutral-200 hover:border-red-400 text-neutral-700 hover:text-red-600 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1">
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </>
                    )}
                    {tab === 'active' && step && (
                      <button
                        data-testid={`cleaner-advance-${j.id}`}
                        onClick={() => advance(j.id, step.next)}
                        disabled={claiming}
                        className="flex-1 py-2 rounded-lg bg-[#D97706] hover:bg-[#B45309] disabled:opacity-50 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1">
                        {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <step.icon className="w-4 h-4" />}
                        {step.label}
                      </button>
                    )}
                    {tab === 'finished' && (
                      <span className="flex-1 py-2 rounded-lg bg-neutral-100 text-neutral-500 text-sm font-semibold text-center">Job completed</span>
                    )}
                  </div>

                  {/* Status timeline (only in Active tab) */}
                  {tab === 'active' && (
                    <div className="mt-3 pt-3 border-t border-neutral-100">
                      <StatusStepper status={j.status} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

const Tab = ({ id, label, active, onClick, testid }) => (
  <button data-testid={testid} onClick={() => onClick(id)}
    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${active ? 'bg-[#2E7D32] text-white' : 'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50'}`}>
    {label}
  </button>
);

const STEPS = ['Accepted', 'EnRoute', 'Started', 'Finished'];
const STEPS_LABEL = ['Accepted', 'En Route', 'Started', 'Finished'];
const StatusStepper = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex items-center justify-between" data-testid="cleaner-job-stepper">
      {STEPS.map((s, i) => {
        const done = i <= idx;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? 'bg-[#2E7D32] text-white' : 'bg-neutral-200 text-neutral-400'}`}>{i + 1}</div>
              <span className={`text-[9px] ${done ? 'text-[#2E7D32] font-semibold' : 'text-neutral-400'}`}>{STEPS_LABEL[i]}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < idx ? 'bg-[#2E7D32]' : 'bg-neutral-200'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CleanerJobs;
